import { IDBPDatabase, openDB } from 'idb';

export const dbName = 'videoCacheDB';
export const storeName = 'videoCacheStore';
export let db: IDBPDatabase<unknown> = null;

// Open the IndexedDB database and store
export async function openDatabase() {
  if (db !== null) return;

  db = await openDB(dbName, 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(storeName)) {
        db.createObjectStore(storeName, {
          autoIncrement: true,
        });
      }
    },
  });

  return db;
}

type ValueCache = {
  name: string;
  url: BlobPart[] | Blob | string;
  type?: 'video' | string;
  oldName?: string;
  id: string;
};

// Check for and restore cached video data
export async function restoreCachedVideo(): Promise<ValueCache[]> {
  const tx = db.transaction(storeName, 'readonly');
  const store = tx.objectStore(storeName);

  // const chunks = await store.get(id);
  const chunks = await store.getAll();
  await tx.done;

  if (!chunks?.length) return;

  // Concatenate the chunks to restore the video data
  return chunks;
  // chunks.forEach((chunk) => callback(chunk));
  // callback(chunks);
}

export async function cacheVideoData(id: string, newValue: ValueCache) {
  if (db === null) return;

  const tx = db.transaction(storeName, 'readwrite');
  const store = tx.objectStore(storeName);

  // Fetch the existing data from IndexedDB
  const existingData: ValueCache = await store.get(id);
  const oldURL = existingData?.url;
  const newURL = newValue?.url;

  if (existingData) {
    // If data with the specified ID exists, update the array
    if (newURL instanceof Blob) {
      (oldURL as BlobPart[]).push(newURL);

      // Store the updated array back in IndexedDB
      await store.put(existingData, id);
    } else {
      await store.put(newValue, id);
    }
  } else {
    // If no data with the specified ID exists, create a new array
    const newArray = newURL instanceof Blob ? [newURL] : newURL;
    await store.add(
      {
        ...newValue,
        url: newArray,
      },
      id
    );
  }

  await tx.done;
}

export async function modifyCacheVideo(id: string, updates: ValueCache) {
  if (db === null) return;

  const tx = db.transaction(storeName, 'readwrite');
  const store = tx.objectStore(storeName);

  // Fetch the existing data from IndexedDB
  const existingData: ValueCache = await store.get(id);

  // If data exists, merge the updates and store them
  if (existingData) {
    // const updatedData = { ...existingData, ...updates };
    await store.put(updates, id);
  } else {
    const newURL = updates?.url;
    const newArray = newURL instanceof Blob ? [newURL] : newURL;
    await store.add(
      {
        ...updates,
        url: newArray,
      },
      id
    );
  }

  await tx.done;
}

export async function cleanDatabase() {
  try {
    const db = await openDB(dbName, 1);
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);

    // Use openCursor to iterate over all records and delete them
    const cursor = await store.openCursor();

    while (cursor) {
      await store.delete(cursor.primaryKey);
      await cursor.continue();
    }

    await tx.done; // Complete the transaction

    // Close the database
    db.close();

    // Update your UI or perform any other necessary actions
  } catch (error) {
    console.error('Error cleaning the database:', error);
  }
}

export function closeDB() {
  if (db) {
    db.close();
    // Set 'db' to null or perform any other necessary cleanup
    db = null;
  }
}

export async function clearByKey(key) {
  try {
    const db = await openDB(dbName, 1);
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const existingData: ValueCache = await store.get(key);

    if (existingData) {
      store.delete(key);
    }
  } catch (error) {
    console.error('Error cleaning the database:', error);
  }
}

export async function clearListCache(list: string[]) {
  return await Promise.all(list.map((key: string) => clearByKey(key)));
}
